import React, { useState } from 'react';
import { Star, Quote } from 'lucide-react';

/**
 * Renders a rating like 4.7 as 4 full stars + a 70%-filled 5th star.
 * Works by stacking a yellow star (clipped to the fill %) over a grey one.
 */
const StarRating = ({ value, sizeClass = 'w-3.5 h-3.5' }) => (
  <div className="flex items-center" role="img" aria-label={`Rated ${value} out of 5`}>
    {[0, 1, 2, 3, 4].map((i) => {
      const fillPercent = Math.max(0, Math.min(1, value - i)) * 100;
      return (
        <span key={i} className="relative inline-block mr-0.5">
          <Star className={`${sizeClass} text-slate-200 fill-current`} />
          <span
            className="absolute inset-y-0 left-0 overflow-hidden"
            style={{ width: `${fillPercent}%` }}
          >
            <Star className={`${sizeClass} text-yellow-500 fill-current max-w-none shrink-0`} />
          </span>
        </span>
      );
    })}
  </div>
);

const ClientTestimonials = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  // Client testimonials from Arch Point Consultants
  // NOTE: `rating` can be any decimal from 0 to 5 (e.g. 4.6, 4.9).
  const testimonials = [
    {
      id: 1,
      name: "Parth Saini",
      position: "Home Owner",
      company: "Residential Project",
      rating: 4.9,
      text: "One of the best architects in Jaipur! Excellent services, professional approach, and very friendly nature. The house plan and commercials were explained in detail, which made everything clear and smooth. Recently got my home completed in Mansarovar, and I'm truly grateful to Arch Point Consultants and Amit Ji for their wonderful work.",
      location: "Mansarovar, Jaipur",
      category: "Residential"
    },
    {
      id: 2,
      name: "SP Bharill",
      position: "Hospital Administrator",
      company: "Amar Jain Hospital",
      rating: 4.8,
      text: "Arch Point Consultants has done an excellent job in completing the Amar Jain Hospital project with architectural, interior, and PMC services, ensuring everything was delivered with precision and professionalism. They have also beautifully managed the interiors of our residence.",
      location: "Jaipur",
      category: "Commercial"
    },
    {
      id: 3,
      name: "Riddhi Kapoor",
      position: "Client",
      company: "Design Consultation",
      rating: 4.7,
      text: "The team at Arch Point Consultants was an absolute delight to work with! Their professionalism, creativity, and collaborative approach made the entire experience seamless. I especially loved the soothing and positive environment they create.",
      location: "Jaipur",
      category: "Consultation"
    },
    {
      id: 4,
      name: "Gopal Sharma",
      position: "Flat Owner",
      company: "Seven Heaven Flats",
      rating: 4.9,
      text: "A big thank you to Arch Point Consultants for completing the interiors of our Seven Heaven flat at Gandhi Path, Mansarovar so beautifully. The design, finishing, and attention to detail have truly exceeded our expectations.",
      location: "Gandhi Path, Mansarovar",
      category: "Interior"
    },
    {
      id: 5,
      name: "Anurag Aman",
      position: "Client",
      company: "Residence Interior",
      rating: 4.6,
      text: "Arch Point Consultants is really an amazing choice for your dream construction, they have a hardworking team which delivers on time and has an aesthetic sense to design your building. Really amazing experience.",
      location: "Jaipur",
      category: "Interior"
    },
    {
      id: 6,
      name: "Vivek Kalra",
      position: "Home Owner",
      company: "Residential Design",
      rating: 4.8,
      text: "Getting our residence designed and executed by Arch Point Consultants has been a very satisfying experience. Right from the first discussion till the final handover, the team was approachable and clear in their process. They guided us at every stage, balanced creativity with practicality.",
      location: "Jaipur",
      category: "Residential"
    },
    {
      id: 7,
      name: "Hetansh Sheth",
      position: "Client",
      company: "Design Consultation",
      rating: 4.7,
      text: "Professional approach and creative solutions, listens to the client's demands and gives an appropriate design. I highly recommend this consultancy for your design needs.",
      location: "Jaipur",
      category: "Consultation"
    }
  ];

  const categories = ['All', ...Array.from(new Set(testimonials.map(t => t.category)))];
  const filtered = activeFilter === 'All'
    ? testimonials
    : testimonials.filter(t => t.category === activeFilter);

  // Average across all testimonials, e.g. "4.8"
  const averageRating = (
    testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length
  ).toFixed(1);

  return (
    <div className="relative py-20 bg-gradient-to-br from-slate-50 via-amber-50 to-yellow-50">
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-yellow-300 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-amber-300 rounded-full blur-3xl opacity-10"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-block px-4 py-2 bg-yellow-100 rounded-full border border-yellow-200 mb-6">
            <span className="text-yellow-700 font-semibold text-sm uppercase tracking-wide">Client Testimonials</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6 leading-tight">
            What Our <span className="text-yellow-600">Valued Clients</span> Say
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Real experiences from real clients who trusted Arch Point Consultants with their architectural dreams in Jaipur and beyond.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                activeFilter === cat
                  ? 'bg-yellow-500 text-white border-yellow-500 shadow-md'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-yellow-300 hover:text-yellow-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Masonry Wall */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {filtered.map((t) => (
            <div
              key={t.id}
              className="break-inside-avoid mb-5 bg-white rounded-2xl p-6 shadow-md border border-yellow-200/50 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-slate-800 leading-tight">{t.name}</h4>
                  <p className="text-slate-500 text-xs leading-tight">{t.position}</p>
                </div>
                <Quote className="w-5 h-5 text-yellow-300 shrink-0" />
              </div>

              {/* Decimal star rating */}
              <div className="flex items-center gap-2 mb-3">
                <StarRating value={t.rating} />
                <span className="text-xs font-semibold text-slate-600">{t.rating.toFixed(1)}</span>
              </div>

              <p className="text-slate-700 text-sm leading-relaxed mb-4">{t.text}</p>

              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <span className="text-yellow-700 font-medium text-xs">{t.company}</span>
                <span className="text-slate-400 text-xs flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  {t.location}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section (commented out; the rating now uses the real average) */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          <div className="text-center bg-white rounded-xl px-6 py-4 shadow-lg border border-yellow-200/50">
            <div className="text-2xl font-bold text-slate-800">{averageRating}/5</div>
            <div className="flex justify-center mb-1">
              <StarRating value={Number(averageRating)} sizeClass="w-4 h-4" />
            </div>
            <p className="text-slate-600 text-sm">Average Rating</p>
            <p className="text-slate-500 text-xs">From {testimonials.length} client reviews</p>
          </div>

          <div className="text-center bg-white rounded-xl px-6 py-4 shadow-lg border border-yellow-200/50">
            <div className="text-2xl font-bold text-slate-800">500+</div>
            <div className="text-yellow-600 font-semibold text-sm">Projects Completed</div>
            <p className="text-slate-500 text-xs">Residential & Commercial</p>
          </div>

          <div className="text-center bg-white rounded-xl px-6 py-4 shadow-lg border border-yellow-200/50">
            <div className="text-2xl font-bold text-slate-800">18+</div>
            <div className="text-yellow-600 font-semibold text-sm">Years Experience</div>
            <p className="text-slate-500 text-xs">In Jaipur & Beyond</p>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default ClientTestimonials;